import {
  Alert,
  Card,
  Divider,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSkeleton from "components/TableSkeleton/TableSkeleton";
import DeliveryRow from "components/ApiTriggerDeliveryRow/ApiTriggerDeliveryRow";
import useGetNotificationTrigger from "hooks/notifications/useGetNotificationTrigger";
import useGetNotificationTriggerDeliveries from "hooks/notifications/useGetNotificationTriggerDeliveries";
import DashboardLayout from "layouts/DashboardLayout/DashboardLayout";
import TestApiDialog from "modules/email-triggers/TestApiDialog/TestApiDialog";
import DashboardNavbar from "partials/DashboardNavbar/DashboardNavbar";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

export default function ApiTriggerDeliveries() {
  const { triggerId } = useParams<{ triggerId: string }>();
  const navigate = useNavigate();
  const { data: trigger, loading: loadingTrigger } = useGetNotificationTrigger({ id: triggerId! });
  const {
    deliveries,
    loading: loadingDeliveries,
    error,
    refetch,
  } = useGetNotificationTriggerDeliveries(triggerId!);
  const [testOpen, setTestOpen] = useState(false);

  const loading = loadingTrigger || loadingDeliveries;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            p={3}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <MDBox>
              <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                <MDTypography
                  variant="caption"
                  color="info"
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate("/email-triggers")}
                >
                  ← Notification Triggers
                </MDTypography>
              </MDBox>
              <MDTypography variant="h5" fontWeight="medium">
                Delivery Logs
              </MDTypography>
              {trigger && (
                <MDTypography variant="caption" color="text" sx={{ fontFamily: "monospace" }}>
                  {trigger.name} — {trigger.apiUrl}
                </MDTypography>
              )}
            </MDBox>
            <MDBox display="flex" gap={1}>
              <MDButton size="small" variant="outlined" color="secondary" onClick={() => refetch()}>
                <Icon sx={{ mr: 0.5 }}>refresh</Icon> Refresh
              </MDButton>
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                onClick={() => setTestOpen(true)}
                disabled={!trigger?.apiUrl}
              >
                <Icon sx={{ mr: 0.5 }}>play_arrow</Icon> Test Trigger
              </MDButton>
            </MDBox>
          </MDBox>

          <Divider sx={{ m: 0 }} />

          {loading && (
            <MDBox p={3}>
              <TableSkeleton numberOfLines={6} />
            </MDBox>
          )}

          {error && (
            <MDBox p={3}>
              <Alert severity="error">{error.message}</Alert>
            </MDBox>
          )}

          {!loading && !error && deliveries.length === 0 && (
            <MDBox p={4} display="flex" flexDirection="column" alignItems="center">
              <Icon sx={{ fontSize: "40px !important", color: "text.secondary", mb: 1 }}>
                inbox
              </Icon>
              <MDTypography variant="body2" color="text">
                No deliveries yet. Test the trigger or wait for an event to fire.
              </MDTypography>
            </MDBox>
          )}

          {!loading && !error && deliveries.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group", p: 0 }}>
                  <TableRow>
                    <TableCell sx={{ width: 32 }} />
                    <TableCell>Status</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>HTTP</TableCell>
                    <TableCell>Attempts</TableCell>
                    <TableCell>Delivered</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deliveries.map((d) => (
                    <DeliveryRow key={d.id} delivery={d} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </MDBox>

      <TestApiDialog
        open={testOpen}
        onClose={() => setTestOpen(false)}
        triggerId={trigger?.id}
        apiUrl={trigger?.apiUrl ?? ""}
      />
    </DashboardLayout>
  );
}
